import {Plugins, Component} from '@packages/systems/wfdl';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  EElement,
  EText,
  EList,
  ERecord,
  type Expression,
  EEnum,
  EBoolean,
  ELiteral,
} from '@packages/systems/wfdl-ast';
import {NodePojo} from '@packages/systems/core/nodepojo';

const createTextElement = (text: string, id = 'String') =>
  EElement({
    id,
    type: qualify(Plugins.name('Basic'), Component.name('String')),
    data: EText(text),
  });

export const defaultButtonChildren: Array<Expression> = [
  createTextElement('Button Text'),
];

export const defaultTextLinkChildren: Array<Expression> = [
  createTextElement('Text Link'),
];

export const defaultHeadingChildren: Array<Expression> = [
  createTextElement('Heading'),
];

export const defaultHeadingChildrenNodePojo: NodePojo[] = [
  {
    id: 'Heading 1',
    type: null,
    text: true,
    data: {value: 'Heading'},
    children: [],
  },
];

export const defaultParagraphChildren: Array<Expression> = [
  createTextElement(
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat. Aenean faucibus nibh et justo cursus id rutrum lorem imperdiet. Nunc ut sem vitae risus tristique posuere.'
  ),
];

export const defaultParagraphChildrenNodePojo: NodePojo[] = [
  {
    id: 'Paragraph 1',
    type: null,
    text: true,
    data: {
      value:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat. Aenean faucibus nibh et justo cursus id rutrum lorem imperdiet. Nunc ut sem vitae risus tristique posuere.',
    },
    children: [],
  },
];

export const defaultStrongChildren: Array<Expression> = [
  createTextElement('bold'),
];

export const defaultEmChildren: Array<Expression> = [
  createTextElement('italic'),
];

export const defaultSupChildren: Array<Expression> = [
  createTextElement('superscript'),
];

export const defaultSubChildren: Array<Expression> = [
  createTextElement('subscript'),
];

export const defaultSpanChildren: Array<Expression> = [
  createTextElement('span'),
];

export const defaultTextDivChildren: Array<Expression> = [
  createTextElement('This is some text inside of a div block.'),
];

export const defaultTextDivChildrenNodePojo: NodePojo[] = [
  {
    id: 'Text block 1',
    type: null,
    text: true,
    data: {value: 'This is some text inside of a div block.'},
    children: [],
  },
];

export const defaultBlockquoteChildren: Array<Expression> = [
  createTextElement('Block Quote'),
];

export const defaultBlockquoteChildrenNodePojo: NodePojo[] = [
  {
    id: 'Block quote 1',
    type: null,
    text: true,
    data: {value: 'Block Quote'},
    children: [],
  },
];

export const defaultRichTextChildren: Array<Expression> = [
  EElement({
    id: 'Heading 1',
    type: qualify(Plugins.name('Basic'), Component.name('Heading')),
    data: ERecord({
      tag: EEnum('h1'),
      children: EList([createTextElement('Heading 1', 'String 1')]),
    }),
  }),
  EElement({
    id: 'Heading 2',
    type: qualify(Plugins.name('Basic'), Component.name('Heading')),
    data: ERecord({
      tag: EEnum('h2'),
      children: EList([createTextElement('Heading 2', 'String 2')]),
    }),
  }),
  EElement({
    id: 'Heading 3',
    type: qualify(Plugins.name('Basic'), Component.name('Heading')),
    data: ERecord({
      tag: EEnum('h3'),
      children: EList([createTextElement('Heading 3', 'String 3')]),
    }),
  }),
  EElement({
    id: 'Heading 4',
    type: qualify(Plugins.name('Basic'), Component.name('Heading')),
    data: ERecord({
      tag: EEnum('h4'),
      children: EList([createTextElement('Heading 4', 'String 4')]),
    }),
  }),
  EElement({
    id: 'Heading 5',
    type: qualify(Plugins.name('Basic'), Component.name('Heading')),
    data: ERecord({
      tag: EEnum('h5'),
      children: EList([createTextElement('Heading 5', 'String 5')]),
    }),
  }),
  EElement({
    id: 'Heading 6',
    type: qualify(Plugins.name('Basic'), Component.name('Heading')),
    data: ERecord({
      tag: EEnum('h6'),
      children: EList([createTextElement('Heading 6', 'String 6')]),
    }),
  }),
  EElement({
    id: 'Paragraph 1',
    type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
    data: ERecord({
      children: EList([
        createTextElement(
          'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.',
          'String 7'
        ),
      ]),
    }),
  }),
  EElement({
    id: 'Block Quote 1',
    type: qualify(Plugins.name('Basic'), Component.name('Blockquote')),
    data: ERecord({
      children: EList([createTextElement('Block quote', 'String 8')]),
    }),
  }),
  EElement({
    id: 'Paragraph 2',
    type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
    data: ERecord({
      children: EList([createTextElement('Ordered list', 'String 9')]),
    }),
  }),
  EElement({
    id: 'List 1',
    type: qualify(Plugins.name('Basic'), Component.name('List')),
    data: ERecord({
      tag: EEnum('ol'),
      list: ERecord({
        type: EText('list'),
        unstyled: EBoolean(false),
      }),
      children: EList([
        EElement({
          id: 'List Item 1',
          type: qualify(Plugins.name('Basic'), Component.name('ListItem')),
          data: ERecord({
            list: ERecord({
              type: EText('item'),
            }),
            children: EList([createTextElement('Item 1', 'String 10')]),
            text: EBoolean(false),
          }),
        }),
        EElement({
          id: 'List Item 2',
          type: qualify(Plugins.name('Basic'), Component.name('ListItem')),
          data: ERecord({
            list: ERecord({
              type: EText('item'),
            }),
            children: EList([createTextElement('Item 2', 'String 11')]),
            text: EBoolean(false),
          }),
        }),
        EElement({
          id: 'List Item 3',
          type: qualify(Plugins.name('Basic'), Component.name('ListItem')),
          data: ERecord({
            list: ERecord({
              type: EText('item'),
            }),
            children: EList([createTextElement('Item 3', 'String 12')]),
            text: EBoolean(false),
          }),
        }),
      ]),
    }),
  }),
  EElement({
    id: 'Paragraph 3',
    type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
    data: ERecord({
      children: EList([createTextElement('Unordered list', 'String 13')]),
    }),
  }),
  EElement({
    id: 'List 2',
    type: qualify(Plugins.name('Basic'), Component.name('List')),
    data: ERecord({
      tag: EEnum('ul'),
      list: ERecord({
        type: EText('list'),
        unstyled: EBoolean(false),
      }),
      children: EList([
        EElement({
          id: 'List Item 4',
          type: qualify(Plugins.name('Basic'), Component.name('ListItem')),
          data: ERecord({
            list: ERecord({
              type: EText('item'),
            }),
            children: EList([createTextElement('Item A', 'String 14')]),
            text: EBoolean(false),
          }),
        }),
        EElement({
          id: 'List Item 5',
          type: qualify(Plugins.name('Basic'), Component.name('ListItem')),
          data: ERecord({
            list: ERecord({
              type: EText('item'),
            }),
            children: EList([createTextElement('Item B', 'String 15')]),
            text: EBoolean(false),
          }),
        }),
        EElement({
          id: 'List Item 6',
          type: qualify(Plugins.name('Basic'), Component.name('ListItem')),
          data: ERecord({
            list: ERecord({
              type: EText('item'),
            }),
            children: EList([createTextElement('Item C', 'String 16')]),
            text: EBoolean(false),
          }),
        }),
      ]),
    }),
  }),
  EElement({
    id: 'Paragraph 4',
    type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
    data: ERecord({
      children: EList([
        EElement({
          id: 'Text Link 1',
          type: qualify(Plugins.name('Basic'), Component.name('Link')),
          data: ERecord({
            button: EBoolean(false),
            block: EText(''),
            eventIds: EList([]),
            children: EList([createTextElement('Text link', 'String 17')]),
            link: ELiteral({
              name: qualify(Plugins.name('Basic'), typeName('Link')),
              value: {
                mode: 'external',
                url: 'https://university.webflow.com/lesson/add-and-nest-text-links-in-webflow',
              },
            }),
          }),
        }),
      ]),
    }),
  }),
  EElement({
    id: 'Paragraph 5',
    type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
    data: ERecord({
      children: EList([
        EElement({
          id: 'Strong Text 1',
          type: qualify(Plugins.name('Basic'), Component.name('Strong')),
          data: ERecord({
            children: EList([createTextElement('Bold text', 'String 18')]),
          }),
        }),
      ]),
    }),
  }),
  EElement({
    id: 'Paragraph 6',
    type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
    data: ERecord({
      children: EList([
        EElement({
          id: 'Emphasis Text 1',
          type: qualify(Plugins.name('Basic'), Component.name('Emphasized')),
          data: ERecord({
            children: EList([createTextElement('Emphasis', 'String 19')]),
          }),
        }),
      ]),
    }),
  }),
  EElement({
    id: 'Paragraph 7',
    type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
    data: ERecord({
      children: EList([
        EElement({
          id: 'Superscript 1',
          type: qualify(Plugins.name('Basic'), Component.name('Superscript')),
          data: ERecord({
            children: EList([createTextElement('Superscript', 'String 20')]),
          }),
        }),
      ]),
    }),
  }),
  EElement({
    id: 'Paragraph 8',
    type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
    data: ERecord({
      children: EList([
        EElement({
          id: 'Subscript 1',
          type: qualify(Plugins.name('Basic'), Component.name('Subscript')),
          data: ERecord({
            children: EList([createTextElement('Subscript', 'String 21')]),
          }),
        }),
      ]),
    }),
  }),
];

// This is a duplicate of `defaultRichTextChildren` but as NodePojos
export const defaultRichTextChildrenNodePojos: NodePojo[] = [
  {
    id: 'Heading 1',
    type: 'Heading',
    text: false,
    data: {tag: 'h1'},
    children: [
      {
        id: 'String 1',
        type: null,
        text: true,
        data: {value: 'Heading 1'},
        children: [],
      },
    ],
  },
  {
    id: 'Heading 2',
    type: 'Heading',
    text: false,
    data: {tag: 'h2'},
    children: [
      {
        id: 'String 2',
        type: null,
        text: true,
        data: {value: 'Heading 2'},
        children: [],
      },
    ],
  },
  {
    id: 'Heading 3',
    type: 'Heading',
    text: false,
    data: {tag: 'h3'},
    children: [
      {
        id: 'String 3',
        type: null,
        text: true,
        data: {value: 'Heading 3'},
        children: [],
      },
    ],
  },
  {
    id: 'Heading 4',
    type: 'Heading',
    text: false,
    data: {tag: 'h4'},
    children: [
      {
        id: 'String 4',
        type: null,
        text: true,
        data: {value: 'Heading 4'},
        children: [],
      },
    ],
  },
  {
    id: 'Heading 5',
    type: 'Heading',
    text: false,
    data: {tag: 'h5'},
    children: [
      {
        id: 'String 5',
        type: null,
        text: true,
        data: {value: 'Heading 5'},
        children: [],
      },
    ],
  },
  {
    id: 'Heading 6',
    type: 'Heading',
    text: false,
    data: {tag: 'h6'},
    children: [
      {
        id: 'String 6',
        type: null,
        text: true,
        data: {value: 'Heading 6'},
        children: [],
      },
    ],
  },
  {
    id: 'Paragraph 1',
    type: 'Paragraph',
    text: false,
    data: {},
    children: [
      {
        id: 'String 7',
        type: null,
        text: true,
        data: {
          value:
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.',
        },
        children: [],
      },
    ],
  },
  {
    id: 'Block Quote 1',
    type: 'Blockquote',
    text: false,
    data: {},
    children: [
      {
        id: 'String 8',
        type: null,
        text: true,
        data: {value: 'Block quote'},
        children: [],
      },
    ],
  },
  {
    id: 'Paragraph 2',
    type: 'Paragraph',
    text: false,
    data: {},
    children: [
      {
        id: 'String 9',
        type: null,
        text: true,
        data: {value: 'Ordered list'},
        children: [],
      },
    ],
  },
  {
    id: 'List 1',
    type: 'List',
    text: false,
    data: {tag: 'ol', list: {type: 'list', unstyled: false}},
    children: [
      {
        id: 'List Item 1',
        type: 'ListItem',
        text: false,
        data: {list: {type: 'item'}, text: false},
        children: [
          {
            id: 'String 10',
            type: null,
            text: true,
            data: {value: 'Item 1'},
            children: [],
          },
        ],
      },
      {
        id: 'List Item 2',
        type: 'ListItem',
        text: false,
        data: {list: {type: 'item'}, text: false},
        children: [
          {
            id: 'String 11',
            type: null,
            text: true,
            data: {value: 'Item 2'},
            children: [],
          },
        ],
      },
      {
        id: 'List Item 3',
        type: 'ListItem',
        text: false,
        data: {list: {type: 'item'}, text: false},
        children: [
          {
            id: 'String 12',
            type: null,
            text: true,
            data: {value: 'Item 3'},
            children: [],
          },
        ],
      },
    ],
  },
  {
    id: 'Paragraph 3',
    type: 'Paragraph',
    text: false,
    data: {},
    children: [
      {
        id: 'String 13',
        type: null,
        text: true,
        data: {value: 'Unordered list'},
        children: [],
      },
    ],
  },
  {
    id: 'List 2',
    type: 'List',
    text: false,
    data: {tag: 'ul', list: {type: 'list', unstyled: false}},
    children: [
      {
        id: 'List Item 4',
        type: 'ListItem',
        text: false,
        data: {list: {type: 'item'}, text: false},
        children: [
          {
            id: 'String 14',
            type: null,
            text: true,
            data: {value: 'Item A'},
            children: [],
          },
        ],
      },
      {
        id: 'List Item 5',
        type: 'ListItem',
        text: false,
        data: {list: {type: 'item'}, text: false},
        children: [
          {
            id: 'String 15',
            type: null,
            text: true,
            data: {value: 'Item B'},
            children: [],
          },
        ],
      },
      {
        id: 'List Item 6',
        type: 'ListItem',
        text: false,
        data: {list: {type: 'item'}, text: false},
        children: [
          {
            id: 'String 16',
            type: null,
            text: true,
            data: {value: 'Item C'},
            children: [],
          },
        ],
      },
    ],
  },
  {
    id: 'Paragraph 4',
    type: 'Paragraph',
    text: false,
    data: {},
    children: [
      {
        id: 'Text Link 1',
        type: 'Link',
        text: false,
        data: {
          button: false,
          block: '',
          link: {
            mode: 'external',
            url: 'https://university.webflow.com/lesson/add-and-nest-text-links-in-webflow',
          },
        },
        children: [
          {
            id: 'String 17',
            type: null,
            text: true,
            data: {value: 'Text link'},
            children: [],
          },
        ],
      },
    ],
  },
  {
    id: 'Paragraph 5',
    type: 'Paragraph',
    text: false,
    data: {},
    children: [
      {
        id: 'Strong Text 1',
        type: 'Strong',
        text: false,
        data: {},
        children: [
          {
            id: 'String 18',
            type: null,
            text: true,
            data: {value: 'Bold text'},
            children: [],
          },
        ],
      },
    ],
  },
  {
    id: 'Paragraph 6',
    type: 'Paragraph',
    text: false,
    data: {},
    children: [
      {
        id: 'Emphasis Text 1',
        type: 'Emphasized',
        text: false,
        data: {},
        children: [
          {
            id: 'String 19',
            type: null,
            text: true,
            data: {value: 'Emphasis'},
            children: [],
          },
        ],
      },
    ],
  },
  {
    id: 'Paragraph 7',
    type: 'Paragraph',
    text: false,
    data: {},
    children: [
      {
        id: 'Superscript 1',
        type: 'Superscript',
        text: false,
        data: {},
        children: [
          {
            id: 'String 20',
            type: null,
            text: true,
            data: {value: 'Superscript'},
            children: [],
          },
        ],
      },
    ],
  },
  {
    id: 'Paragraph 8',
    type: 'Paragraph',
    text: false,
    data: {},
    children: [
      {
        id: 'Subscript 1',
        type: 'Subscript',
        text: false,
        data: {},
        children: [
          {
            id: 'String 21',
            type: null,
            text: true,
            data: {value: 'Subscript'},
            children: [],
          },
        ],
      },
    ],
  },
];
